import { pendingFocusItemIdAtom } from '@pique/core/atoms';
import { openActionItemInKanbanSlot } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';
import { ActionItemsService } from '@pique/core/modules/properties';
import {
  ACTION_ITEM_STATUS,
  type ActionItemStatus,
} from '@pique/core/modules/properties/services/schema';
import { WorkbenchService } from '@pique/core/modules/workbench';
import { useLiveData, useService } from '@toeverything/infra';
import { useAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  POMODORO_POPUP_ACTION_EVENT,
  POMODORO_POPUP_STATE_EVENT,
  type PomodoroPopupActionPayload,
  type PomodoroPopupStatePayload,
} from '../../pages/pomodoro-popup-events';

export const PomodoroPopupBridge = () => {
  const actionItemsService = useService(ActionItemsService);
  const workbenchService = useService(WorkbenchService);
  const actionItemsByPage = useLiveData(actionItemsService.actionItems$);
  const focusedItemId = useLiveData(actionItemsService.focusedItemId$);
  const focusedSessionStartedAtMs = useLiveData(
    actionItemsService.focusedSessionStartedAtMs$
  );
  const [pendingFocusItemId, setPendingFocusItemId] = useAtom(
    pendingFocusItemIdAtom
  );
  const userMinimizedRef = useRef(false);
  const prevFocusedItemIdRef = useRef<string | null>(null);
  const popupVisibleRef = useRef(false);

  useEffect(() => {
    if (!pendingFocusItemId) return;
    const started = actionItemsService.startFocusSession(pendingFocusItemId);
    if (started) {
      setPendingFocusItemId(null);
    }
  }, [actionItemsService, pendingFocusItemId, setPendingFocusItemId]);

  const activeItem = useMemo(() => {
    if (!focusedItemId) return null;
    return actionItemsService.findById(focusedItemId) ?? null;
  }, [actionItemsByPage, actionItemsService, focusedItemId]);

  const emitPopupState = useCallback(
    (payload: PomodoroPopupStatePayload | null) => {
      if (!environment.isTauri) return;
      import('@tauri-apps/api/event')
        .then(({ emit }) => emit(POMODORO_POPUP_STATE_EVENT, payload))
        .catch(() => {});
    },
    []
  );

  const debouncedShowPopup = useMemo(
    () =>
      debounce(
        async (payload: PomodoroPopupStatePayload) => {
          const { commands } = await import('../../tauri/plugins/windows');
          if (!popupVisibleRef.current) {
            await commands.windowShow({ type: 'pomodoro-popup' });
            popupVisibleRef.current = true;
            window.setTimeout(() => emitPopupState(payload), 200);
          }
          emitPopupState(payload);
        },
        300,
        { leading: true, trailing: false }
      ),
    [emitPopupState]
  );

  const debouncedDestroyPopup = useMemo(
    () =>
      debounce(
        async () => {
          const { commands } = await import('../../tauri/plugins/windows');
          emitPopupState(null);
          popupVisibleRef.current = false;
          await commands.windowDestroy({ type: 'pomodoro-popup' });
        },
        300,
        { leading: true, trailing: false }
      ),
    [emitPopupState]
  );

  useEffect(() => {
    if (!environment.isTauri) return;

    const prevId = prevFocusedItemIdRef.current;
    const isNewSession = focusedItemId !== null && prevId === null;

    if (focusedItemId !== prevId) {
      if (isNewSession) {
        userMinimizedRef.current = false;
        popupVisibleRef.current = false;
      }
      prevFocusedItemIdRef.current = focusedItemId;
    }

    const hasActiveSession =
      Boolean(focusedItemId) && typeof focusedSessionStartedAtMs === 'number';

    if (!focusedItemId || !hasActiveSession) {
      debouncedShowPopup.cancel();
      debouncedDestroyPopup();
      popupVisibleRef.current = false;
      return;
    }

    if (!activeItem) {
      return;
    }

    if (userMinimizedRef.current) {
      return;
    }

    const payload: PomodoroPopupStatePayload = {
      itemId: activeItem.id,
      title:
        typeof activeItem.title === 'string'
          ? activeItem.title
          : String(activeItem.title || 'Focus timer'),
      pageId: activeItem.pageId || undefined,
      plannedDuration: activeItem.plannedDuration,
      focusDaily: activeItem.focusDaily ?? undefined,
      focusTimeSeconds: activeItem.focusTimeSeconds ?? undefined,
      focusedSessionStartedAtMs,
      isActive: hasActiveSession,
    };

    debouncedDestroyPopup.cancel();
    debouncedShowPopup(payload);
  }, [
    activeItem,
    debouncedDestroyPopup,
    debouncedShowPopup,
    focusedItemId,
    focusedSessionStartedAtMs,
  ]);

  useEffect(() => {
    return () => {
      debouncedShowPopup.cancel();
      debouncedDestroyPopup.cancel();
    };
  }, [debouncedShowPopup, debouncedDestroyPopup]);

  useEffect(() => {
    if (!environment.isTauri) return;

    let unlisten: null | (() => void) = null;
    import('@tauri-apps/api/event')
      .then(({ listen }) =>
        listen<PomodoroPopupActionPayload>(
          POMODORO_POPUP_ACTION_EVENT,
          event => {
            const payload = event.payload;
            if (!payload) return;

            if (payload.type === 'minimize') {
              userMinimizedRef.current = true;
              popupVisibleRef.current = false;
              debouncedShowPopup.cancel();
              import('../../tauri/plugins/windows')
                .then(({ commands }) =>
                  commands.windowHide({ type: 'pomodoro-popup' })
                )
                .catch(() => {});
              return;
            }

            if (!('itemId' in payload) || !payload.itemId) return;

            if (payload.type === 'toggle') {
              if (payload.isActive) {
                actionItemsService.setFocusedItem(null);
              } else {
                actionItemsService.setFocusedItem(payload.itemId);
              }
              return;
            }

            if (payload.type === 'complete') {
              actionItemsService.setFocusedItem(null);
              const item = actionItemsService.findById(payload.itemId);
              const breakdownSteps = item?.breakdownSteps;
              const hasBreakdownSteps =
                Array.isArray(breakdownSteps) && breakdownSteps.length > 0;

              if (hasBreakdownSteps) {
                const completedSteps = breakdownSteps.map(step => ({
                  ...step,
                  completed: true,
                }));
                actionItemsService.updateById(payload.itemId, {
                  breakdownSteps: completedSteps,
                  progress: 100,
                  status: ACTION_ITEM_STATUS.DONE as ActionItemStatus,
                });
              } else {
                actionItemsService.updateById(payload.itemId, {
                  progress: 100,
                  status: ACTION_ITEM_STATUS.DONE as ActionItemStatus,
                });
              }
              return;
            }

            if (payload.type === 'open') {
              import('../../tauri/plugins/windows')
                .then(({ commands }) => commands.windowShow({ type: 'main' }))
                .catch(() => {});

              const item = actionItemsService.findById(payload.itemId);
              const pageId = payload.pageId || item?.pageId;
              if (!pageId) return;

              workbenchService.workbench.openDoc(
                { docId: pageId, mode: 'page' },
                {
                  at: 'active',
                  onComplete: () => {
                    openActionItemInKanbanSlot.emit({
                      pageId,
                      itemId: payload.itemId,
                    });
                  },
                }
              );
            }
          }
        )
      )
      .then(stop => {
        unlisten = stop;
      })
      .catch(() => {});
    return () => {
      if (unlisten) unlisten();
    };
  }, [actionItemsService, debouncedShowPopup, workbenchService.workbench]);

  return null;
};
